<template>
    <div class="main-container p--0">
        <div class="w-100 relative h--100">    
            <LandingHeader :no-inner-padding="false" class="pt--10" page-type="public_profile" />

            <template v-if="pageState === 'data_loading'">
                <div>
                    <PageSpinner />
                </div>
            </template>
                            
            <template v-else-if="pageState === 'data_error'">
                <div class="mt--40">
                    <EmptyPage 
                        state="error_custom" 
                        btn-state="none"
                        :title="pageErrMsg.title"
                        :subtitle="pageErrMsg.sub" 
                    />
                </div>
            </template>
        
            <template v-else>
                <div class="profile-body">
                    <section class="profile-wrap p-6 md:p-8 pb--0">
                        <div class="profile-card flex flex-col align-items-center">
                            <div>
                                <CustomAvatar
                                    border-style="solid" 
                                    width="100px" 
                                    :initials="initials" 
                                    :src="profileUrl"
                                    class="text--lg profile-icon"
                                />
                            </div>
    
                            <h1 class="profile-title">
                                {{ profile?.fullName }}
                            </h1>
    
                            <div class="mb--5">
                                <span class="profile-stats">{{ profile?.email }}</span>
                                <span class="profile-stats ml--3 mr--3">|</span>
                                <span class="text--capitalize profile-stats">{{ profile?.occupation }}</span>
                            </div>
    
                            <div class="flex align-items-center">
                                <p class="profile-stats">
                                    {{ profile?.completedProjectCount }} projects completed
                                </p> 
                                <p class="profile-stats ml--3 mr--3">
                                    |
                                </p>
                                <p class="profile-stats">
                                    {{ profile?.onboardedClientsCount }} clients onboarded
                                </p>
                            </div>

                            <header v-if="publicShareUrl" class="py-2 px-3 flex align-items-center justify-content-between z_p_header">
                                <div class="flex align-items-center mr--15">
                                    <span class="profile-link ml-2 text--sm">{{ publicShareUrl }}</span>
                                </div>
            
                                <div class="flex items-center space-x-2">
                                    <div class="dropdown">
                                        <BaseButton 
                                            label="Share"
                                            prefix-icon
                                            class="text--sm"
                                            icon-name="share"
                                            aria-expanded="false"
                                            data-bs-toggle="dropdown" 
                                            data-bs-auto-close="true" 
                                        />

                                        <List id="noteDropdown">
                                            <ListItem flex pointer @click="handleCopyNoteContent()">
                                                <IconSvg class="mr--10" name="copy" fill="#64748B" /> 
                                                <span ref="copyLinkInput" class="mb--0">Copy link</span>
                                            </ListItem>
                                            <ListItem flex pointer @click="handleShareOnWhatsapp">
                                                <IconSvg class="mr--10" name="whatsapp_square" fill="#64748B" /> 
                                                <span class="mb--0">Share via WhatsApp</span>
                                            </ListItem>
                                            <ListItem flex pointer @click="handleShareOnTwitter">
                                                <IconSvg class="mr--10" name="x_square" width="16px" height="16px" />
                                                <span class="mb--0">Share via X</span>
                                            </ListItem>
                                            <ListItem flex pointer @click="handleShareOnLinkedIn">
                                                <IconSvg class="mr--10" name="linkedin_square" width="16px" height="16px" /> 
                                                <span class="mb--0">Share via LinkedIn</span>
                                            </ListItem>
                                        </List>
                                    </div>
                                </div>
                            </header>
                        </div>
                    </section>

                    <section class="profile-wrap p-6 md:p-8 pb--0">
                        <div class="profile-card">
                            <h3>
                                Bio
                            </h3>
                            <p>
                                {{ profile?.bio }}
                            </p>
                        </div>
                    </section>

                    <section class="profile-wrap p-6 md:p-8 pb--0">
                        <div class="profile-card">
                            <h3>
                                Portfolio
                            </h3>
                 
                            <div v-if="profile?.socials && profile.socials.length">
                                <p class="text--sm text-gray-600 mb--10">
                                    Social links
                                </p>
                                <div class="flex space-x-3 flex-wrap">
                                    <a 
                                        v-for="item in profile.socials" 
                                        :key="item._id" 
                                        :href="item.url" 
                                        target="_blank" 
                                        class="p-2 border border-dark pointer rounded-full transition duration-150 mr--15"
                                    >
                                        <IconSvg 
                                            class="h-5 w-5 text-gray-700" 
                                            fill="#4a3b31" 
                                            :name="item.name" 
                                            width="24px" 
                                            height="24px" 
                                        />
                                    </a>
                                </div>
                            </div>
                 
                            <div v-if="profile?.skills && profile.skills.length" class="mt--15">
                                <p class="text--sm text-gray-600">
                                    Skills
                                </p>
                                <div class="flex flex-wrap justify-center gap-2 mt--15">
                                    <span 
                                        v-for="skill in profile.skills" 
                                        :key="skill" 
                                        class="tag text--xs px-3 py-1 rounded-full text-white"
                                    >
                                        {{ skill }}
                                    </span>
                                </div>
                            </div>
                 
                            <div v-if="profile?.links && profile.links.length" class="mt--20">
                                <p class="text--sm text-gray-600 mb--10">
                                    Links
                                </p>
             
                                <a v-for="link in profile.links" :key="link._id" href="#" class="text-gray-200 text--underline profile-links flex items-center pt-1 pb-1 text--sm transition duration-150">
                                    <span class="text-sm md:text-base font-medium mb--5">{{ link.name }}</span>
                                </a>
                            </div>
                        </div>
                    </section>
                                
                    <section v-if="profile?.city || profile?.country" class="profile-wrap p-6 md:p-8 pb--0">
                        <div class="profile-card">
                            <h3>
                                Location
                            </h3>
                            <p>
                                {{ profile?.city }}, {{ profile?.country }}
                            </p>
                        </div>
                    </section>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import LandingHeader from "@/components/home/LandingHeader.vue";
import { 
    IconSvg, EmptyPage, PageSpinner, CustomAvatar,
    BaseButton, List, ListItem, 
} from "@/components/ui/index";
import env from "@/config/env";
import api from "@/api/url";

export default {
    name: "ProfilePage",

    components: {
        BaseButton,
        IconSvg,
        CustomAvatar,
        EmptyPage,
        PageSpinner,
        List,
        ListItem,
        BaseButton,
        LandingHeader,
    },

    beforeRouteEnter(to, from, next) {        
        next(async(vm) => {            
            if (to.name === "public-profile") {    
                if (!vm.profile) {
                    return await vm.getPublicData();
                }
            }
        });
    },

    data() {
        return {
            pageState: "",
            errMsg: "",
            profile: null,
        };
    },

    computed: {
        initials() {
            return this.$initials(this.profile?.fullName);
        },

        profileUrl() {
            return this.profile?.profileImageUrl || "";
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        publicShareUrl() {              
            if (!this.profile?.slug) return "";          
            
            return `${env.VUE_APP_CLIENT_URL}/public/${this.profile?.slug}`;
        },

        pageErrMsg() {
            if (this.errMsg.includes("ERR_FAILED_002")) {
                return {
                    title: "Profile not found",
                    sub: "The profile you are looking for does not exist or has been deleted.",
                };
            }

            if (this.errMsg.includes("ERR_US_02")) {
                return {
                    title: "Profile not found",
                    sub: "The profile you are looking for does not exist or has been deleted.",
                };
            }

            return {
                title: "Page error",
                sub: "",
            };
        },
    },

    methods: {
        async getPublicData() {            
            try {              
                const getStoredProfile = localStorage.getItem("z_public_profile");
                
                if (getStoredProfile) {
                    this.profile = JSON.parse(getStoredProfile);
                    this.pageState = "";
                    return;
                }

                this.errMsg = "";
                this.pageState = "data_loading";
                
                const res = await api.app.fetchPublicProfile({ slug: this.$route.params?.name });
                this.profile = res?.data?.data || null;
                localStorage.setItem("z_public_profile", JSON.stringify(res.data.data));
                this.pageState = "";
            } catch (error) {                                                
                this.pageState = "data_error";
                const errMsg = error?.response?.data?.message || error?.message;  
                
                console.log("Error fetching public profile:", errMsg);
                
                this.errMsg = errMsg;
            }
        },

        handleCopyNoteContent() {
            navigator.clipboard.writeText(this.publicShareUrl);
        },

        handleShareOnTwitter() {
            if (!this.publicShareUrl) {
                this.$toast.error("No profile URL to share.");
                return;
            }

            const urlToShare = this.publicShareUrl;
            const textToShare = "Check out my profile on Ziner!";

            const encodedUrl = encodeURIComponent(urlToShare);
            const encodedText = encodeURIComponent(textToShare);

            const twitterShareUrl = `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
            window.open(twitterShareUrl, "_blank", "width=550,height=450,toolbar=0,scrollbars=0,status=0,resizable=0,location=0,menubar=0");
        },

        handleShareOnLinkedIn() {
            if (!this.publicShareUrl) {
                this.$toast.error("No profile URL to share.");
                return;
            }

            const encodedUrl = encodeURIComponent(this.publicShareUrl);
            const encodedText = encodeURIComponent("Check out my profile on Ziner!");
            // const title = ""

            // const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedText}`;
            const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
            window.open(linkedInShareUrl, "_blank");
        },

        handleShareOnWhatsapp() {
            if (!this.publicShareUrl) {
                this.$toast.error("No profile URL to share.");
                return;
            }

            const encodedUrl = encodeURIComponent(this.publicShareUrl);
            const encodedText = encodeURIComponent("Check out my profile on Ziner!");
            const desktop = window.innerWidth > 768;
            let shareUrl;

            if (desktop) {
                shareUrl = `https://web.whatsapp.com/send?text=${encodedText} ${encodedUrl}`;
            } else {
                shareUrl = `whatsapp://send?text=${encodedText} ${encodedUrl}`;
            }

            window.open(shareUrl, "_blank", "width=550,height=450,toolbar=0,scrollbars=0,status=0,resizable=0,location=0,menubar=0");
        },
    },
};
</script>

<style lang="scss" scoped>
    .z_p_header {
        border-radius: 30px;
        align-items: center;
        display: flex;
        margin: 1.5rem 1.5rem 1rem;
        border: 1px solid #eee;
    }

    .profile {
        &-body {
            max-width: 800px;
            margin: auto;
            padding: 3rem 0;
        }

        &-wrap {
            gap: 25px 0px;
            display: grid;
        }

        &-title {
            font-size: 32px;
            font-weight: 700;
            color: #2C3E50;
            margin-bottom: 8px;
            margin-top: 20px;
        }

        &-stats {
            color: #7F8C8D;
            font-size: 14px;
        }

        &-card {
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            text-align: left;
            width: 100%;

            h3 {
                font-size: 20px;
                font-weight: 600;
                color: #2C3E50;
                margin-bottom: 20px;
            }

            p, span {
                color: #5A6C7D;
                line-height: 1.6;
                margin-bottom: 16px;
                font-size: 0.875rem;
            }
        }

        &-icon {
            --tw-border-opacity: 1;
            border-color: rgb(255 255 255 / var(--tw-border-opacity, 1));
            border-width: 4px;
            border-radius: 9999px;
            width: 6rem;
            height: 6rem;
            box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
            --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
            --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
            box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
            border-style: solid;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: 600;
            color: #4a3b31;
        }

        &-content {
            margin-left: 240px;
            height: calc(100% - 20px);
            overflow-y: scroll;
            padding-left: 15px;
            border-left: 1px solid #eee
        }

        &-link {
            white-space: nowrap;
            text-overflow: ellipsis;
            max-width: 300px;
            overflow: hidden;
            margin-bottom: 0 !important;
        }
    }

    .main-container {
        margin: 0 auto; 
        background: linear-gradient(90deg, #fdd6cd, #edb9ff);
        height: auto;
        width: 100%;
        min-height: 100vh;
    }

    .tag {
        background-color: #333333;
        color: white;
    }

    ::-webkit-scrollbar {
        width: 8px;
    }

    ::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    ::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
</style>